import { createContext, useCallback, useContext, useRef, useState } from "react";
import { X } from "lucide-react";

const Ctx = createContext(null);
let zCounter = 1000;

export function FloatingCardsProvider({ children }) {
  const [cards, setCards] = useState([]);
  const counter = useRef(0);

  const open = useCallback((id, component, props = {}, opts = {}) => {
    setCards((prev) => {
      const existing = prev.find((c) => c.id === id);
      if (existing) return prev.map((c) => (c.id === id ? { ...c, component, props, z: ++zCounter } : c));
      const n = counter.current++;
      const offset = (n % 6) * 28;
      return [...prev, { id, component, props, width: opts.width || 520, x: opts.x ?? 120 + offset, y: opts.y ?? 80 + offset, z: ++zCounter }];
    });
  }, []);

  const close = useCallback((id) => setCards((prev) => prev.filter((c) => c.id !== id)), []);
  const focus = useCallback((id) => setCards((prev) => prev.map((c) => (c.id === id ? { ...c, z: ++zCounter } : c))), []);
  const move = useCallback((id, x, y) => setCards((prev) => prev.map((c) => (c.id === id ? { ...c, x, y } : c))), []);

  return (
    <Ctx.Provider value={{ open, close }}>
      {children}
      {cards.map((c) => (
        <FloatingCard key={c.id} card={c} onClose={() => close(c.id)} onFocus={() => focus(c.id)} onMove={(x, y) => move(c.id, x, y)} />
      ))}
    </Ctx.Provider>
  );
}

export const useFloatingCards = () => useContext(Ctx);

function FloatingCard({ card, onClose, onFocus, onMove }) {
  const drag = useRef(null);
  const Comp = card.component;

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    onFocus();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: card.x, oy: card.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const nx = Math.max(0, Math.min(window.innerWidth - 80, drag.current.ox + e.clientX - drag.current.sx));
    const ny = Math.max(0, Math.min(window.innerHeight - 40, drag.current.oy + e.clientY - drag.current.sy));
    onMove(nx, ny);
  };
  const onPointerUp = () => { drag.current = null; };

  return (
    <div
      data-testid={`floating-card-${card.id}`}
      className="floating-card fixed fade-in flex flex-col"
      style={{ left: card.x, top: card.y, width: card.width, zIndex: card.z, maxHeight: "calc(100vh - 32px)" }}
      onMouseDown={onFocus}
    >
      <div
        data-testid={`floating-card-${card.id}-drag`}
        className="h-7 shrink-0 cursor-grab active:cursor-grabbing flex items-center justify-between px-3 select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className="w-10 h-1 rounded-full bg-border mx-auto" />
        <button
          data-testid={`floating-card-${card.id}-close`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="absolute right-2 top-1.5 rounded-md p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto px-4 pb-4">
        <Comp {...card.props} close={onClose} />
      </div>
    </div>
  );
}
